<script setup lang="ts">
import {
	ref,
	reactive,
	provide,
	computed,
	onMounted,
	watch,
	nextTick,
} from 'vue'
import { useStore } from '@nanostores/vue'
import axios from 'axios'
import InAppSpy from 'inapp-spy'
import InApp from 'detect-inapp'
import useHashQuery from '@/legacy/ts/useHashQuery'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import AuthEntrance from '@/components/auth/AuthEntrance.vue'
import AuthLogin from '@/components/auth/AuthLogin.vue'
import AuthSignup from '@/components/auth/AuthSignup.vue'
import AuthOtp from '@/components/auth/AuthOtp.vue'
import AuthSuccess from '@/components/auth/AuthSuccess.vue'
import AuthForget from '@/components/auth/AuthForget.vue'
import AuthReset from '@/components/auth/AuthReset.vue'
import loginHelper from '@/assets/ts/loginHelper'
import { updatePassword, updateUserFields } from '@/assets/ts/apiManagement'
import { isLogin } from '@/store/userStore'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { checkIsPhActivation, isGlobalSite } from '@/lib/checkService'
import { getCurrentGaPageName } from '@/lib/utils'
// inapp-solution
import { useAppOpener } from '@/lib/useAppOpener'

enum StepMapping {
	ENTRANCE = 'entrance',
	OTP = 'otp',
	SIGNUP = 'signup',
	LOGIN = 'login',
	FORGET = 'forget',
	RESET = 'reset',
	SUCCESS = 'success',
	RESET_STATUS = 'resetStatus',
}

enum OtpTypeMapping {
	LOGIN = 'login',
	SIGNUP = 'signup',
	FORGET = 'forget',
}

enum SuccessTypeMapping {
	LOGIN_VERIFY = 'loginVerify',
	RESET_PASSWORD = 'resetPassword',
	SIGNUP_NORMAL = 'signupNormal',
	SIGNUP_BUYFORYOU = 'signupBuyforyou',
	SIGNUP_EC = 'signupEc',
	SIGNUP_MERCARI_BRAND = 'signupMercariBrand',
}

enum TypeProviderMapping {
	BASIC = 'basic',
	EMAIL_OTP = 'email-otp',
	GOOGLE = 'google',
	FACEBOOK = 'facebook',
	APPLE = 'apple',
}

enum TypeSignupMapping {
	NORMAL = 'normal',
	SOCIAL = 'social',
}

enum TypeSuccessDialogMapping {
	BNS = 'bns',
	B4U = 'b4u',
	EASYBUY = 'easybuy',
	MERCARI_BRAND = 'mercariBrand',
}

const props = defineProps({
	text: Object,
	isShowAuthDialog: {
		type: Boolean,
		default: false,
	},
	typeSuccessDialog: {
		type: String,
		default: TypeSuccessDialogMapping.BNS,
	},
	isExposeEntranceOnly: {
		type: Boolean,
		default: false,
	},
})

// inapp-solution
const { isInApp } = InAppSpy()
const { openApp } = useAppOpener()

const { searchParameter } = useHashQuery()

provide('textContent', props.text)

const isLoginGlobal = useStore(isLogin)

const step = ref(StepMapping.RESET_STATUS)
const defaultStep = ref(StepMapping.ENTRANCE)

const otpType = ref(OtpTypeMapping.LOGIN)
const successType = ref(SuccessTypeMapping.LOGIN_VERIFY)
const currentProvider = ref<TypeProviderMapping>(TypeProviderMapping.EMAIL_OTP)
const email = ref('')
const formLoginPassword = ref('')
const formLoginToken = ref('')

const typeSignup = ref(TypeSignupMapping.NORMAL)
const formSignupFullName = ref('')
const formSignupPassword = ref('')
const formSignupInviteCode = ref('')
const formSignupToken = ref('')
const formSignupNewsletter = ref(true)
const formSignupLivingCountry = ref('')

const tokenForget = ref('')
const passwordReset = ref('')

const errorApiMessageBasicLogin = ref('')
const errorApiMessageBasicSignup = ref('')
const errorApiMessageSocial = ref('')
const errorApiMessageResetPassword = ref('')
const errorApiMessageUpdateField = ref('')
const errorApiMessageNoFullName = ref('')
const googleData = reactive({
	token: '',
	loginStatus: '',
	state: {
		redirect: '',
		successType: '',
	},
})
const successList = Object.values(SuccessTypeMapping)

const isTypeSignupSocial = computed(
	() => typeSignup.value === TypeSignupMapping.SOCIAL,
)
const currentSignupTypeSuccess = computed(() => {
	return getCurrentSuccessType(props.typeSuccessDialog)
})
const successUrlSignup = computed(() => {
	if (googleData.state.redirect) {
		if (isGlobalSite()) {
			return setAndGetUrlParams({
				redirect: googleData.state.redirect,
			})
		}

		const leadingSymbol = googleData.state.redirect.includes('?') ? '&' : '?'
		return setAndGetUrlParams({
			redirect: `${
				googleData.state.redirect
			}${leadingSymbol}successType=${getCurrentSuccessType(
				googleData.state.successType,
			)}&panel=auth`,
		})
	}
	// To hide Auth Success dialog go verify dialog on Global Site
	if (isGlobalSite()) {
		return window.location.href
	}
	return getSuccessUrl(currentSignupTypeSuccess.value)
})

const successUrlLogin = computed(() => {
	// To hide Auth Success dialog go verify dialog on Global Site
	if (isGlobalSite()) {
		return window.location.href
	}
	return getSuccessUrl(SuccessTypeMapping.LOGIN_VERIFY)
})

const successUrlResetPassword = computed(() =>
	getSuccessUrl(SuccessTypeMapping.RESET_PASSWORD),
)

const isGoogleLoginSuccess = computed(
	() => googleData.loginStatus === 'google-login-success',
)

function hideAuthPanel() {
	removeSuccessQueryUrl()
	nextTick(() => {
		window.BsHeaderVm.hideAuthPanel()
	})
}

function toEntrance() {
	step.value = StepMapping.ENTRANCE
}
function toLogin() {
	step.value = StepMapping.LOGIN
}
function toSignup(isSocial = false) {
	if (isSocial) {
		typeSignup.value = TypeSignupMapping.SOCIAL
	} else {
		typeSignup.value = TypeSignupMapping.NORMAL
	}
	step.value = StepMapping.SIGNUP
}
function toOtp(type = OtpTypeMapping.LOGIN) {
	step.value = StepMapping.OTP
	otpType.value = type
}
function toForget() {
	step.value = StepMapping.FORGET
}
function toReset() {
	step.value = StepMapping.RESET
}
function toSuccess(type = SuccessTypeMapping.SIGNUP_NORMAL) {
	successType.value = type
	step.value = StepMapping.SUCCESS
}
function resetErrorMessageBasicLogin() {
	errorApiMessageBasicLogin.value = ''
}
function resetErrorMessageSocial() {
	errorApiMessageSocial.value = ''
}
function resetErrorMessageSignup() {
	errorApiMessageBasicSignup.value = ''
}
function resetErrorMessageResetPassword() {
	errorApiMessageResetPassword.value = ''
}
function resetErrorApiMessageUpdateField() {
	errorApiMessageUpdateField.value = ''
}
function resetErrorApiMessageNoFullName() {
	errorApiMessageNoFullName.value = ''
}
function resetErrorMessagesAll() {
	resetErrorMessageBasicLogin()
	resetErrorMessageSignup()
	resetErrorMessageSocial()
	resetErrorMessageResetPassword()
	resetErrorApiMessageUpdateField()
	resetErrorApiMessageNoFullName()
}

function removeQueryUrl(list: string[] = [], isRemoveHash = false) {
	if (!list.length) {
		return
	}
	const url = new URL(window.location.href)
	list.forEach(key => {
		url.searchParams.delete(key)
	})
	if (isRemoveHash) {
		url.hash = ''
	}
	window.history.replaceState({}, '', url.href)
}

function removeSuccessQueryUrl() {
	return removeQueryUrl(['successType', 'panel'])
}

function removeGoogleSuccessQueryUrl() {
	return removeQueryUrl(['status', 'panel'], true)
}

function updateEmail(value = '') {
	email.value = value
}

async function onLogin(params: {
	type: 'password' | 'token'
	password: string
	token: string
}) {
	if (params.type === 'password') {
		formLoginPassword.value = params.password
	} else {
		formLoginToken.value = params.token
	}

	loginBasic(params.type)
}

function onSubmitEntrance(params: { email: string; type: 'login' | 'signup' }) {
	email.value = params.email
	if (params.type === 'login') {
		toLogin()
	} else {
		toOtp(OtpTypeMapping.SIGNUP)
	}
}

function onSubmitForget(params: { email: string }) {
	email.value = params.email
	toOtp(OtpTypeMapping.FORGET)
}

function onOtpSubmitForget(params: { token: string }) {
	tokenForget.value = params.token
	toReset()
}

function onOtpSubmitSignup(params: { token: string }) {
	formSignupToken.value = params.token
	toSignup()
}

function onSubmitResetPassword(params: { password: string }) {
	passwordReset.value = params.password
	resetErrorMessageResetPassword()
	return updatePassword({
		token: tokenForget.value,
		password: passwordReset.value,
		email: email.value,
	})
		.then(() => {
			toSuccessResetPasswordUrl()
		})
		.catch((err: any) => {
			errorApiMessageResetPassword.value = err.message
			throw err
		})
}

function toSuccessSignupUrl() {
	const shouldNotShowOnboarding =
		props.typeSuccessDialog === TypeSuccessDialogMapping.EASYBUY ||
		props.typeSuccessDialog === TypeSuccessDialogMapping.B4U ||
		props.typeSuccessDialog === TypeSuccessDialogMapping.MERCARI_BRAND

	const shouldShowOnboarding = checkIsPhActivation() && !shouldNotShowOnboarding

	const baseUrl = getSiteBaseUrl()
	const isInvitePage = getCurrentGaPageName() === GaGeneralPageName.InvitePage
	const url =
		shouldShowOnboarding && !isInvitePage
			? `${baseUrl}/onboarding`
			: successUrlSignup.value

	if (shouldShowOnboarding) {
		localStorage.setItem('show_onboarding_page', 'true')
	}

	if (isInvitePage) {
		localStorage.setItem('show_invite_success_page', 'true')
	}

	window.history.replaceState({}, '', url)
	window.BsHeaderVm.login({
		provider: currentProvider.value,
		new_account: 'yes',
	})
}

function toSuccessResetPasswordUrl() {
	window.location.href = successUrlResetPassword.value
}

function updateUserField() {
	resetErrorApiMessageUpdateField()
	resetErrorApiMessageNoFullName()
	return updateUserFields({
		fullName: formSignupFullName.value,
		inviteCode: formSignupInviteCode.value,
		newsletter: transformNewsletter(formSignupNewsletter.value),
		livingCountry: formSignupLivingCountry.value,
	})
		.then(() => {
			toSuccessSignupUrl()
		})
		.catch((err: any) => {
			errorApiMessageUpdateField.value = err.message
			throw err
		})
}

function setAndGetUrlParams(
	params: { [key: string]: string },
	initUrl = window.location.href,
) {
	const url = new URL(initUrl)

	Object.entries(params).forEach(([key, value]) => {
		url.searchParams.set(key, value)
	})

	return url.href
}

function getSuccessUrl(successTypeTo: SuccessTypeMapping) {
	return setAndGetUrlParams({
		successType: successTypeTo,
		panel: 'auth',
	})
}

function setSocialErrorMessage(from = '') {
	errorApiMessageSocial.value = props.text?.api_error_social_login.replace(
		'{provider}',
		from,
	)
}

function loginBasic(type: 'password' | 'token' = 'password') {
	resetErrorMessageBasicLogin()
	loginHelper.clearToken()

	setCurrentProvider(
		type === 'password'
			? TypeProviderMapping.BASIC
			: TypeProviderMapping.EMAIL_OTP,
	)

	const form: {
		email: string
		provider: TypeProviderMapping
		password?: string
		token?: string
	} = {
		email: email.value,
		provider: currentProvider.value,
	}

	if (type === 'password') {
		form.password = formLoginPassword.value
	} else {
		form.token = formLoginToken.value
	}

	axios
		.post(`${window.BUYANDSHIP.settings.api_url}sessions`, form)
		.then(async ({ data }) => {
			if (type === 'password') {
				createGAEvent('password_login_success', {
					...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
				})
			}

			setLastLoginFrom(currentProvider.value)
			window.BsHeaderVm.login({
				...eventParameterFormat(data.data),
				provider: currentProvider.value,
			})
			hideAuthPanel()
		})
		.catch(err => {
			let message = 'System is busy, please try later'
			if (
				err.response &&
				err.response.data &&
				err.response.data.error &&
				err.response.data.error.message
			) {
				message = err.response.data.error.message
			}
			errorApiMessageBasicLogin.value = message
		})
}

function onSignup(params: {
	fullName: string
	password: string
	invite: string
	newsletter: boolean
	livingCountry: string
}) {
	formSignupFullName.value = params.fullName
	formSignupPassword.value = params.password
	formSignupInviteCode.value = params.invite
	formSignupNewsletter.value = params.newsletter
	formSignupLivingCountry.value = params.livingCountry
	if (isTypeSignupSocial.value) {
		updateUserField()
	} else {
		signupBasic()
	}
}

/**
 * Transforms a boolean value to 'yes' or 'no' string.
 * This function is used to convert the newsletter subscription status
 * to a format expected by the API or other parts of the application.
 *
 * @param value - The boolean value to transform
 * @returns 'yes' if the input is true, 'no' otherwise
 */
function transformNewsletter(value: boolean) {
	return value ? 'yes' : 'no'
}

function signupBasic() {
	setCurrentProvider(TypeProviderMapping.EMAIL_OTP)
	axios
		.post(`${window.BUYANDSHIP.settings.api_url}users/signup`, {
			email: email.value,
			password: formSignupPassword.value,
			full_name: formSignupFullName.value,
			invite_code: formSignupInviteCode.value,
			token: formSignupToken.value,
			newsletter: transformNewsletter(formSignupNewsletter.value),
			provider: currentProvider.value,
			living_country: formSignupLivingCountry.value,
		})
		.then(() => {
			resetErrorMessageSignup()
			createGAEvent('sign_up_success', {
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
			setLastLoginFrom(currentProvider.value)
			toSuccessSignupUrl()
		})
		.catch(err => {
			let message = 'System is busy, please try later'
			if (
				err.response &&
				err.response.data &&
				err.response.data.error &&
				err.response.data.error.message
			) {
				message = err.response.data.error.message
			}
			errorApiMessageBasicSignup.value = message
		})
}

function setLastLoginFrom(from: `${TypeProviderMapping}`) {
	localStorage.setItem('lastLoginMethod', from)
}

function requestFacebookSDK() {
	if (interceptInAppRequest()) {
		return
	}

	handleFacebookLogin()
}

function interceptInAppRequest() {
	const inapp = new InApp(
		navigator.userAgent || navigator.vendor || window.opera,
	)

	if (isInApp && inapp.isMobile) {
		openApp()

		return true
	}

	return false
}

function handleFacebookLogin() {
	resetErrorMessageSocial()
	loginHelper
		.requestFacebookSDK()
		.then(([res, isNewUser]: [res: any, isNewUser: Boolean]) => {
			setCurrentProvider(TypeProviderMapping.FACEBOOK)
			setLastLoginFrom(currentProvider.value)
			if (isNewUser) {
				createGAEvent('sign_up_success_facebook', {
					...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
				})
				toSignup(true)
			} else {
				window.BsHeaderVm.login({
					...eventParameterFormat(res),
					provider: currentProvider.value,
				})
			}
		})
		.catch(({ message }: { message: string }) => {
			setSocialErrorMessage('Facebook')
		})
}

function googleSignIn() {
	if (interceptInAppRequest()) {
		return
	}

	resetErrorMessageSocial()
	loginHelper.googleSignIn(props.typeSuccessDialog)
}

function onGoogleSignIn() {
	if (!googleData.token) {
		setSocialErrorMessage('Google')
		return
	}
	loginHelper.clearToken()
	setCurrentProvider(TypeProviderMapping.GOOGLE)
	loginHelper
		.requestLogin({
			token: googleData.token,
			provider: currentProvider.value,
		})
		.then(({ data }: { data: any }) => {
			const responseData = data.data || {}
			removeGoogleSuccessQueryUrl()
			setLastLoginFrom(currentProvider.value)

			if (responseData.new_account && responseData.new_account === 'yes') {
				createGAEvent('sign_up_success_google', {
					...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
				})
				toSignup(true)
			} else {
				if (googleData.state.redirect) {
					const urlRedirect = setAndGetUrlParams({
						redirect: googleData.state.redirect,
					})
					window.history.replaceState({}, '', urlRedirect)
				}
				window.BsHeaderVm.login({
					...eventParameterFormat(responseData),
					provider: currentProvider.value,
				})
			}
		})
		.catch(({ error }: { error: string }) => {
			console.log(error)
			setSocialErrorMessage('Google')
		})
}

function initializeAppleSignIn() {
	if (window.AppleID) {
		window.AppleID.auth.init({
			clientId:
				window.BUYANDSHIP.settings.member_auth.social_login.apple.id.web,
			scope: 'name email',
			redirectURI: `${getSiteBaseUrl()}/?panel=login&status=apple-login-success`,
			usePopup: true, // More secure way to authenticate
		})
	}
}

async function signInApple() {
	try {
		if (interceptInAppRequest()) {
			return
		}
		const appleResponse = await window.AppleID.auth.signIn()

		setCurrentProvider(TypeProviderMapping.APPLE)

		const loginResponse = await loginHelper.requestLogin({
			token: appleResponse.authorization.code,
			provider: currentProvider.value,
		})

		const responseData = loginResponse.data?.data || {}

		setLastLoginFrom(currentProvider.value)

		if (responseData?.new_account === 'yes') {
			createGAEvent('sign_up_success_apple', {
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
			toSignup(true)
		} else {
			window.BsHeaderVm.login({
				...eventParameterFormat(responseData),
				provider: currentProvider.value,
			})
		}
	} catch (error: any) {
		console.error('Error:', error)
		setSocialErrorMessage('Apple')
	}
}

function eventParameterFormat(obj: any) {
	return Object.keys(obj).length
		? Object.keys(obj).reduce((acc: any, key) => {
				if (key !== 'user_token') acc[key] = obj[key]
				return acc
		  }, {})
		: {}
}

function clickNoFullName() {
	errorApiMessageNoFullName.value = props.text?.toast_message_full_name_required
}

function initGoogleCheck() {
	const parsedUrl = new URL(window.location.href)
	const params = new URLSearchParams(parsedUrl.search)
	const hashParams = new URLSearchParams(parsedUrl.hash.slice(1))

	googleData.loginStatus = params.get('status') || ''
	googleData.token = hashParams.get('access_token') || ''

	if (!isGoogleLoginSuccess.value) {
		return
	}

	interface ITempState {
		redirect?: string
		successType?: string
	}

	const decodeState = decodeURIComponent(hashParams.get('state') || '')

	if (decodeState) {
		try {
			const parsedState: ITempState = JSON.parse(decodeState)

			if (Object.keys(parsedState).length) {
				googleData.state.redirect = parsedState.redirect || ''
				googleData.state.successType = parsedState.successType || ''
			}
		} catch (error) {
			console.error('Invalid JSON in state:', error)
		}
	}
}

function getCurrentSuccessType(type: string) {
	if (type === TypeSuccessDialogMapping.EASYBUY) {
		return SuccessTypeMapping.SIGNUP_EC
	}
	if (type === TypeSuccessDialogMapping.B4U) {
		return SuccessTypeMapping.SIGNUP_BUYFORYOU
	}
	if (type === TypeSuccessDialogMapping.MERCARI_BRAND) {
		return SuccessTypeMapping.SIGNUP_MERCARI_BRAND
	}
	return SuccessTypeMapping.SIGNUP_NORMAL
}

function setCurrentProvider(provider: TypeProviderMapping) {
	currentProvider.value = provider
}

function resetDialog() {
	step.value = StepMapping.RESET_STATUS
	if (props.isExposeEntranceOnly) {
		toEntrance()
	}
}

onMounted(() => {
	if (props.isExposeEntranceOnly) {
		toEntrance()
		return
	}
	initGoogleCheck()
	if (isGoogleLoginSuccess.value) {
		onGoogleSignIn()
	}
	// Load Apple Sign-In script
	const script = document.createElement('script')
	script.src =
		'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
	script.onload = initializeAppleSignIn
	document.head.appendChild(script)
})

watch(
	() => step.value,
	value => {
		if (value === StepMapping.ENTRANCE) {
			if (props.isExposeEntranceOnly) return
			createGAEvent(GaGeneralEvent.LoginSignupStart, {
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
		} else if (value === StepMapping.LOGIN) {
			createGAEvent('login_start', {
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
		}
		if (value !== StepMapping.ENTRANCE) {
			resetErrorMessagesAll()
		}
	},
)

watch(
	() => props.isShowAuthDialog,
	value => {
		if (value) {
			step.value = defaultStep.value
		} else {
			resetDialog()
		}
	},
)

watch(
	() => searchParameter.value,
	value => {
		if (props.isExposeEntranceOnly) {
			return
		}
		const panel = value.find(query => query.key === 'panel')
		const successTypeFromUrl = value.find(query => query.key === 'successType')

		if (!successTypeFromUrl && isLoginGlobal.value && panel !== undefined) {
			removeSuccessQueryUrl()
			return
		}

		if (panel !== undefined) {
			nextTick(() => {
				window.BsHeaderVm.showAuthPanel()
			})
		}

		if (
			successTypeFromUrl !== undefined &&
			successList.includes(successTypeFromUrl.val as SuccessTypeMapping)
		) {
			defaultStep.value = StepMapping.SUCCESS

			if (
				isLoginGlobal.value &&
				(successTypeFromUrl.val === SuccessTypeMapping.RESET_PASSWORD ||
					!window.BUYANDSHIP.settings.user_onboarded)
			) {
				toSuccess(successTypeFromUrl.val as SuccessTypeMapping)
			} else {
				hideAuthPanel()
			}

			setTimeout(() => {
				defaultStep.value = StepMapping.ENTRANCE
			})
		}
	},
	{
		immediate: true,
	},
)

defineExpose({
	googleSignIn,
	resetDialog,
})
</script>

<template>
	<div v-show="isShowAuthDialog || isExposeEntranceOnly">
		<AuthEntrance
			v-if="step === StepMapping['ENTRANCE']"
			:emailKeep="email"
			:errorApiMessage="errorApiMessageSocial"
			:isExposeEntranceOnly="isExposeEntranceOnly"
			@updateEmail="updateEmail"
			@signInGoogle="googleSignIn"
			@signInFacebook="requestFacebookSDK"
			@signInApple="signInApple"
			@submit="onSubmitEntrance"
			@hide="hideAuthPanel"
		/>
		<AuthLogin
			v-if="step === StepMapping['LOGIN']"
			:errorApiMessage="errorApiMessageBasicLogin"
			@backStep="toEntrance"
			@toOtp="toOtp(OtpTypeMapping['LOGIN'])"
			@toForget="toForget"
			@submit="onLogin"
			@hide="hideAuthPanel"
		/>
		<AuthSignup
			v-if="step === StepMapping['SIGNUP']"
			:type="typeSignup"
			:errorApiMessage="
				errorApiMessageBasicSignup ||
				errorApiMessageUpdateField ||
				errorApiMessageNoFullName
			"
			@submit="onSignup"
			@backStep="toOtp"
			@clickNoFullName="clickNoFullName"
			@hide="hideAuthPanel"
		/>
		<AuthForget
			v-if="step === StepMapping['FORGET']"
			:emailLogin="email"
			@backStep="toLogin"
			@toOtp="toOtp(OtpTypeMapping['FORGET'])"
			@submit="onSubmitForget"
			@updateEmail="updateEmail"
			@hide="hideAuthPanel"
		/>
		<AuthReset
			v-if="step === StepMapping['RESET']"
			:errorApiMessage="errorApiMessageResetPassword"
			@backStep="toOtp(OtpTypeMapping['FORGET'])"
			@submit="onSubmitResetPassword"
			@hide="hideAuthPanel"
		/>
		<AuthOtp
			v-if="step === StepMapping['OTP']"
			:email="email"
			:type="otpType"
			@backStep="toEntrance"
			@backStepForget="toForget"
			@onOtpSubmitLogin="onLogin"
			@onOtpSubmitSignup="onOtpSubmitSignup"
			@onOtpSubmitForget="onOtpSubmitForget"
			@toLogin="toLogin"
			@hide="hideAuthPanel"
		/>
		<AuthSuccess
			v-if="step === StepMapping['SUCCESS']"
			:type="successType"
			@hide="hideAuthPanel"
		/>
	</div>
</template>
